import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Offcanvas } from 'react-bootstrap'
import { URL as BACKEND_URL } from '../../gql/client'
import { useGlobalWebSocket } from '../../providers/WebSocketProvider'
import { useLatency } from '../../hooks/useLatency'
import { useGetUser } from '../../store/userStore'

interface VicidialHealth {
  ok: boolean
  rtt_ms: number | null
  jitter_ms: number | null
  min_ms: number | null
  max_ms: number | null
  error?: string
}

interface SystemHealth {
  broker_ok: boolean
  queue_depth: number | null
  active_workers: number
  active_tasks: number
}

type Status = 'good' | 'warn' | 'error' | 'loading' | 'idle'

const STATUS_COLOR: Record<Status, string> = {
  good: '#50cd89',
  warn: '#ffc700',
  error: '#f1416c',
  loading: '#009ef7',
  idle: '#b5b5c3',
}

const WS_THRESHOLDS = { good: 100, warn: 300 }
const HTTP_THRESHOLDS = { good: 150, warn: 400 }
const VICIDIAL_THRESHOLDS = { good: 200, warn: 500 }
const AUTO_REFRESH_SEC = 30

function getRttStatus(rtt: number | null, thresholds: { good: number; warn: number }): Status {
  if (rtt === null) return 'idle'
  if (rtt < thresholds.good) return 'good'
  if (rtt < thresholds.warn) return 'warn'
  return 'error'
}

const jitterColor = (j: number | null) =>
  j === null ? '#b5b5c3' : j < 10 ? '#50cd89' : j < 30 ? '#ffc700' : '#f1416c'

const mono: React.CSSProperties = { fontFamily: "'SF Mono','Fira Code','Consolas',monospace" }
const sectionLabel: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#b5b5c3',
  padding: '14px 0 4px',
}

function MetricRow({
  label,
  rtt,
  jitter,
  min,
  max,
  status,
}: {
  label: string
  rtt: number | null
  jitter: number | null
  min: number | null
  max: number | null
  status: Status
}) {
  const color = STATUS_COLOR[status]
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f1f2' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color,
              flexShrink: 0,
              transition: 'background-color 0.3s',
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#3f4254' }}>{label}</span>
        </div>
        <span style={{ ...mono, fontSize: 22, fontWeight: 700, color: '#1c2333' }}>
          {rtt === null ? '—' : rtt}
          {rtt !== null && (
            <span style={{ fontSize: 11, color: '#b5b5c3', marginLeft: 2 }}>ms</span>
          )}
        </span>
      </div>

      {rtt !== null && (
        <div style={{ display: 'flex', gap: 16, paddingLeft: 16, marginTop: 5 }}>
          <span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#b5b5c3', marginRight: 3 }}>jitter</span>
            <span style={{ ...mono, fontSize: 11, color: jitterColor(jitter), fontWeight: 700 }}>
              {jitter === null ? '—' : `${jitter}ms`}
            </span>
          </span>
          <span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#b5b5c3', marginRight: 3 }}>min</span>
            <span style={{ ...mono, fontSize: 11, color: '#5e6278' }}>{min === null ? '—' : `${min}ms`}</span>
          </span>
          <span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#b5b5c3', marginRight: 3 }}>max</span>
            <span style={{ ...mono, fontSize: 11, color: '#5e6278' }}>{max === null ? '—' : `${max}ms`}</span>
          </span>
        </div>
      )}
    </div>
  )
}

interface Props {
  isOpen: boolean
  onClose: () => void
}

export const SystemMonitorDrawer = ({ isOpen, onClose }: Props) => {
  const socket = useGlobalWebSocket()
  const { result: latency, measure } = useLatency(socket)
  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')

  const [running, setRunning] = useState(false)
  const [vicidial, setVicidial] = useState<VicidialHealth | null>(null)
  const [system, setSystem] = useState<SystemHealth | null>(null)
  const [serverLoading, setServerLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SEC)
  const countdownRef = useRef<number | null>(null)

  const fetchServerHealth = useCallback(async () => {
    if (!isSuperAdmin) return
    setServerLoading(true)
    try {
      const [vRes, sRes] = await Promise.allSettled([
        fetch(`${BACKEND_URL}/health/vicidial?samples=5`).then((r) => r.json()),
        fetch(`${BACKEND_URL}/health/system`).then((r) => r.json()),
      ])
      if (vRes.status === 'fulfilled') setVicidial(vRes.value as VicidialHealth)
      if (sRes.status === 'fulfilled') setSystem(sRes.value as SystemHealth)
      setLastUpdated(new Date())
    } finally {
      setServerLoading(false)
    }
  }, [isSuperAdmin])

  const runAll = useCallback(async () => {
    setCountdown(AUTO_REFRESH_SEC)
    await Promise.all([measure(), fetchServerHealth()])
  }, [measure, fetchServerHealth])

  // Auto-start on open, auto-stop on close
  useEffect(() => {
    if (isOpen) {
      setRunning(true)
    } else {
      setRunning(false)
    }
  }, [isOpen])

  // Measurements: only when running
  useEffect(() => {
    if (!running) return
    runAll()
    const interval = window.setInterval(runAll, AUTO_REFRESH_SEC * 1000)
    return () => window.clearInterval(interval)
  }, [running]) // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown ticker
  useEffect(() => {
    if (!running) {
      if (countdownRef.current) window.clearInterval(countdownRef.current)
      return
    }
    countdownRef.current = window.setInterval(() => {
      setCountdown((c) => (c <= 1 ? AUTO_REFRESH_SEC : c - 1))
    }, 1000)
    return () => {
      if (countdownRef.current) window.clearInterval(countdownRef.current)
    }
  }, [running])

  const isLoading = latency.measuring || serverLoading
  const wsStatus: Status = latency.measuring ? 'loading' : getRttStatus(latency.wsRtt, WS_THRESHOLDS)
  const httpStatus: Status = latency.measuring ? 'loading' : getRttStatus(latency.httpRtt, HTTP_THRESHOLDS)
  const vicidialStatus: Status = serverLoading ? 'loading' : !vicidial ? 'idle' : !vicidial.ok ? 'error' : getRttStatus(vicidial.rtt_ms, VICIDIAL_THRESHOLDS)

  return (
    <Offcanvas show={isOpen} onHide={onClose} placement='end' style={{ width: 420 }}>
      <Offcanvas.Header closeButton style={{ borderBottom: '1px solid #f1f1f2' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', paddingRight: 8 }}>
          <div style={{ flex: 1 }}>
            <Offcanvas.Title style={{ fontSize: 15, fontWeight: 700, marginBottom: 0 }}>
              System Monitor
            </Offcanvas.Title>
            {lastUpdated && (
              <div style={{ ...mono, fontSize: 10, color: '#b5b5c3', marginTop: 1 }}>
                {lastUpdated.toLocaleTimeString('es', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {running && (
              <span
                style={{
                  ...mono,
                  fontSize: 10,
                  color: '#009ef7',
                  background: '#f1faff',
                  padding: '2px 8px',
                  borderRadius: 20,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {isLoading ? 'midiendo…' : `próximo ${countdown}s`}
              </span>
            )}
            <Button
              size='sm'
              variant={running ? 'light' : 'primary'}
              style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', whiteSpace: 'nowrap' }}
              onClick={() => setRunning((r) => !r)}
            >
              {running ? '⏹ Stop' : '▶ Start'}
            </Button>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ padding: '0 20px 20px' }}>
        {/* Tu conexión */}
        <div style={sectionLabel}>Tu conexión</div>

        <MetricRow
          label='WebSocket'
          rtt={latency.wsRtt}
          jitter={latency.wsJitter}
          min={latency.wsMin}
          max={latency.wsMax}
          status={wsStatus}
        />
        <MetricRow
          label='HTTP / REST'
          rtt={latency.httpRtt}
          jitter={latency.httpJitter}
          min={latency.httpMin}
          max={latency.httpMax}
          status={httpStatus}
        />

        {/* Servidor (solo SuperAdministrador) */}
        {isSuperAdmin && (
          <>
            <div style={sectionLabel}>Servidor</div>

            <MetricRow
              label='Vicidial API'
              rtt={vicidial?.rtt_ms ?? null}
              jitter={vicidial?.jitter_ms ?? null}
              min={vicidial?.min_ms ?? null}
              max={vicidial?.max_ms ?? null}
              status={vicidialStatus}
            />

            {system && (
              <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f1f2' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: system.broker_ok ? '#50cd89' : '#f1416c',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#3f4254' }}>
                      Celery / Valkey
                    </span>
                  </div>
                  <span
                    style={{
                      ...mono,
                      fontSize: 12,
                      fontWeight: 700,
                      color: system.broker_ok ? '#50cd89' : '#f1416c',
                    }}
                  >
                    {system.broker_ok ? 'CONECTADO' : 'ERROR'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 20, paddingLeft: 16, marginTop: 6 }}>
                  {[
                    { value: system.queue_depth ?? '—', label: 'en cola', color: (system.queue_depth ?? 0) >= 10 ? '#ffc700' : '#50cd89' },
                    { value: system.active_workers, label: 'workers', color: system.active_workers > 0 ? '#50cd89' : '#f1416c' },
                    { value: system.active_tasks, label: 'activos', color: '#3f4254' },
                  ].map(({ value, label, color }) => (
                    <span key={label}>
                      <span style={{ ...mono, fontSize: 14, fontWeight: 700, color }}>{value}</span>
                      {' '}
                      <span style={{ fontSize: 11, color: '#b5b5c3' }}>{label}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Referencia de jitter */}
        <div
          style={{
            marginTop: 20,
            padding: '12px 14px',
            background: '#f9f9f9',
            borderRadius: 8,
          }}
        >
          <div style={{ ...sectionLabel, padding: '0 0 8px' }}>Referencia jitter (VoIP)</div>
          {(
            [
              ['< 10ms', '#50cd89', 'Excelente'],
              ['10–30ms', '#ffc700', 'Aceptable'],
              ['> 30ms', '#f1416c', 'Causa problemas en llamadas'],
            ] as const
          ).map(([range, color, label]) => (
            <div key={range} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
              <span style={{ ...mono, fontSize: 11, color: '#5e6278', minWidth: 50 }}>{range}</span>
              <span style={{ fontSize: 11, color: '#b5b5c3' }}>{label}</span>
            </div>
          ))}
        </div>

        {latency.error && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#fff5f8',
              borderLeft: '3px solid #f1416c',
              borderRadius: 4,
              fontSize: 12,
              color: '#3f4254',
            }}
          >
            {latency.error}
          </div>
        )}

        {!running && (
          <div style={{ textAlign: 'center', marginTop: 32, color: '#b5b5c3', fontSize: 13 }}>
            Presiona <strong>▶ Start</strong> para iniciar las mediciones
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}
